import React, { useEffect, useState, useCallback, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Bell, Search, LogOut, Key, Menu, CheckCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { NAV, ADMIN_GROUPS } from "./navConfig";
import "./shell.css";
import {SimulationBanner} from '../components/simulation/SimulationBanner';

const LOGO = "https://customer-assets.emergentagent.com/job_prodfabrix/artifacts/mi0mx8zz_image.png";

function Bell_({ navigate }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/api/notifications/inbox?limit=30").then(r => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, [load]);
  const markAll = async () => { await api.post("/api/notifications/read", { all: true }); load(); };
  return (
    <div style={{ position: "relative" }}>
      <button className="sh-iconbtn" onClick={() => setOpen(o => !o)} data-testid="shell-bell-button" title="Notifications">
        <Bell size={19} />{data.unread > 0 && <span className="sh-badge" data-testid="shell-bell-unread">{data.unread}</span>}
      </button>
      {open && (
        <div className="sh-pop" data-testid="shell-inbox">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #212B3B" }}>
            <strong style={{ fontFamily: "Outfit" }}>Inbox</strong>
            <button className="btn2 sm" onClick={markAll} data-testid="shell-inbox-mark-all"><CheckCheck size={14} /> Mark all read</button>
          </div>
          {data.items.length === 0 && <div className="empty">Nothing new. You're up to date.</div>}
          {data.items.map((n, i) => (
            <a key={i} href={n.link || "#"} className={`sh-pop-row ${n.read ? "" : "unread"}`} data-testid={`inbox-row-${i}`}
               onClick={(e) => { e.preventDefault(); setOpen(false); if (n.link) navigate(n.link); }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontWeight: 600, color: n.severity === "error" ? "#F87171" : n.severity === "warning" ? "#FBBF24" : "#E2E8F0" }}>{n.title}</span>
                <span style={{ color: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }}>{new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              {n.message && <div style={{ color: "#94A3B8", marginTop: 4 }}>{n.message}</div>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function GlobalSearch({ navigate }) {
  const [q, setQ] = useState(""); const [res, setRes] = useState(null); const t = useRef();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    clearTimeout(t.current);
    t.current = setTimeout(() => api.get(`/api/mgmt/search?q=${encodeURIComponent(q.trim())}`).then(r => setRes(r.data)).catch(() => setRes(null)), 300);
  }, [q]);
  const groups = res ? Object.entries(res).filter(([, v]) => v.length) : [];
  return (
    <div style={{ position: "relative" }}>
      <div className="sh-search"><Search size={16} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search WO, item, order, PO…" data-testid="shell-search-input" /></div>
      {res && q.length >= 2 && (
        <div className="sh-pop" style={{ left: 0, right: "auto", width: 480 }} data-testid="shell-search-results">
          {groups.length === 0 && <div className="empty">No matches for “{q}”</div>}
          {groups.map(([k, rows]) => (
            <div key={k}>
              <div className="sh-side-label">{k.replace("_", " ")}</div>
              {rows.map((r, i) => (
                <a key={i} className="sh-pop-row" href={r.link} onClick={(e) => { e.preventDefault(); setQ(""); navigate(r.link); }} data-testid={`search-${k}-${i}`}>
                  <strong style={{ fontFamily: "JetBrains Mono" }}>{r.wo_id || r.item_id || r.order_id || r.po_number || r.po_id}</strong>
                  <span style={{ color: "#94A3B8" }}> · {r.item_name || r.name || r.dealer || r.supplier_name} {r.status ? `· ${r.status}` : ""}</span>
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const isTV = new URLSearchParams(location.search).get("tv") === "true";
  const role = user?.role || "";
  const groups = role === "Admin" ? ADMIN_GROUPS : [{ label: role, items: NAV[role] || [] }];

  if (isTV) return <div className="sh-main"><SimulationBanner/><Outlet /></div>;
  return (
    <div className={`sh-app ${collapsed ? "collapsed" : ""}`} data-testid="app-shell">
      {mustChangePassword && <ChangePasswordModal forced onClose={() => {}} />}
      {showPwd && !mustChangePassword && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
      <header className="sh-top">
        <div className="sh-brand-tools" style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <button className="sh-iconbtn" onClick={() => setCollapsed(c => !c)} data-testid="shell-collapse-nav"><Menu size={19} /></button>
          <div className="sh-brand" onClick={() => navigate("/")} style={{ cursor: "pointer" }} data-testid="shell-brand">
            <img src={LOGO} alt="Sharda" />
            <div><div className="sh-brand-name">Sharda Production</div><div className="sh-brand-sub">ERP · {user?.role_label || role || "—"}{user?.department ? ` · ${user.department}` : ""}</div></div>
          </div>
          <GlobalSearch navigate={navigate} />
        </div>
        <div className="sh-account-tools" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Bell_ navigate={navigate} />
          <button className="sh-iconbtn" onClick={() => setShowPwd(true)} title="Change password" data-testid="shell-change-password"><Key size={18} /></button>
          <div className="sh-user" onClick={() => navigate("/profile")} data-testid="shell-user-menu">
            <div className="sh-avatar">{(user?.full_name || user?.username || "?").slice(0, 1).toUpperCase()}</div>
            <div><div className="sh-user-name">{user?.full_name || user?.username}</div><div className="sh-user-role" data-testid="signed-in-role">{user?.role_label || role}</div></div>
          </div>
          <button className="sh-iconbtn" onClick={logout} title="Sign out" data-testid="shell-logout"><LogOut size={18} /></button>
        </div>
      </header>
      {collapsed&&<button type="button" className="sh-mobile-scrim" onClick={()=>setCollapsed(false)} data-testid="shell-mobile-close-nav" aria-label="Close navigation"/>}
      <aside className="sh-side" data-testid="shell-nav">
        {groups.map(g => (
          <div key={g.label}>
            <div className="sh-side-label">{g.label}</div>
            {g.items.map(it => {
              const Icon = it.icon;
              const active = location.pathname + location.search === it.to || (location.pathname === it.to.split("?")[0] && !it.to.includes("?"));
              return (
                <NavLink key={it.to} to={it.to} end className={`sh-nav ${active ? "active" : ""}`} data-testid={it.testId} title={it.label} onClick={()=>{if(window.innerWidth<=900)setCollapsed(false);}}>
                  <span className="sh-ico"><Icon size={18} /></span><span className="sh-lbl">{it.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </aside>
      <main className="sh-main"><SimulationBanner/><Outlet /></main>
    </div>
  );
}
